import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { useLenis } from "lenis/react";

export const Lightbox = ({ images, captions, index, onClose, onNavigate }) => {
  const lenis = useLenis();

  useEffect(() => {
    lenis?.stop();
    document.body.style.overflow = "hidden";
    return () => {
      lenis?.start();
      document.body.style.overflow = "";
    };
  }, [lenis]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNavigate(index + 1);
      if (e.key === "ArrowLeft") onNavigate(index - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, onClose, onNavigate]);

  const total = images.length;
  const current = ((index % total) + total) % total;

  return (
    <motion.div
      data-testid="lightbox-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
      className="fixed inset-0 z-[100] bg-plum/[0.97] backdrop-blur-md flex flex-col"
      onClick={onClose}
    >
      <div className="flex items-center justify-between px-6 md:px-12 py-6">
        <span data-testid="lightbox-counter" className="text-[11px] tracking-[0.35em] text-cream/80">
          {String(current + 1).padStart(2, "0")} — {String(total).padStart(2, "0")}
        </span>
        <button
          data-testid="lightbox-close-btn"
          onClick={onClose}
          className="text-cream/70 hover:text-cream transition-colors duration-300 p-2"
          aria-label="Close"
        >
          <X size={26} strokeWidth={1.25} />
        </button>
      </div>

      <div className="flex-1 relative flex items-center justify-center px-6 md:px-24 min-h-0" onClick={(e) => e.stopPropagation()}>
        <button
          data-testid="lightbox-prev-btn"
          onClick={() => onNavigate(current - 1)}
          className="absolute start-4 md:start-10 z-10 text-cream/60 hover:text-cream transition-colors duration-300 p-3"
          aria-label="Previous"
        >
          <ChevronLeft size={36} strokeWidth={1} className="rtl:-scale-x-100" />
        </button>

        <AnimatePresence mode="wait">
          <motion.figure
            key={current}
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="max-h-full flex flex-col items-center"
          >
            <motion.img
              src={images[current]}
              alt={captions[current] || ""}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.15}
              onDragEnd={(e, info) => {
                if (info.offset.x < -80) onNavigate(current + 1);
                else if (info.offset.x > 80) onNavigate(current - 1);
              }}
              className="max-h-[72vh] max-w-full object-contain cursor-grab active:cursor-grabbing select-none"
              draggable={false}
            />
            {captions[current] && (
              <figcaption data-testid="lightbox-caption" className="mt-6 font-serif italic text-lg md:text-xl text-cream/80">
                {captions[current]}
              </figcaption>
            )}
          </motion.figure>
        </AnimatePresence>

        <button
          data-testid="lightbox-next-btn"
          onClick={() => onNavigate(current + 1)}
          className="absolute end-4 md:end-10 z-10 text-cream/60 hover:text-cream transition-colors duration-300 p-3"
          aria-label="Next"
        >
          <ChevronRight size={36} strokeWidth={1} className="rtl:-scale-x-100" />
        </button>
      </div>

      <div className="pb-8 flex justify-center gap-1.5">
        {images.map((_, i) => (
          <button
            key={i}
            data-testid={`lightbox-dot-${i}`}
            onClick={(e) => { e.stopPropagation(); onNavigate(i); }}
            className={`h-px w-6 transition-colors duration-300 ${i === current ? "bg-cream" : "bg-cream/25"}`}
            aria-label={`Image ${i + 1}`}
          />
        ))}
      </div>
    </motion.div>
  );
};
